// Request util for api call
import request from '~/utils/request';

// Actions Types
import { SET_TABLE_PREFERENCE, SET_TABLE_PREFERENCE_SAVING } from './action-types';
import { failure } from './snackbar-actions';

// Utils
import { MESSAGES } from '~/utils/message';
import {
  getDefaultVisibleColumns,
  getVisibleColumns,
  toColumnPreference,
} from '~/utils/tablePreferences';

const setPreference = (screen, visibleColumns) => ({
  type: SET_TABLE_PREFERENCE,
  payload: { screen, visibleColumns },
});

const setSaving = (screen, saving) => ({
  type: SET_TABLE_PREFERENCE_SAVING,
  payload: { screen, saving },
});

/**
 * Save the visible columns of a screen. The change shows at once and is rolled back if the request
 * fails. Choosing the default columns resets the saved preference instead of storing it.
 * PUT / DELETE /table-preferences/:screen
 */
const saveTablePreference = (screen, visibleColumns) => async (dispatch, getState) => {
  const previous =
    getState().tablePreferencesReducers.screens[screen]?.visibleColumns ||
    getDefaultVisibleColumns(screen);

  dispatch(setPreference(screen, visibleColumns));
  dispatch(setSaving(screen, true));

  try {
    const endpoint = `table-preferences/${screen}`;
    const preference = toColumnPreference(screen, visibleColumns);
    const isDefault = !preference.hiddenColumns.length && !preference.addedColumns.length;
    const response = isDefault
      ? await request.delete(endpoint)
      : await request.put(endpoint, preference);

    dispatch(setPreference(screen, getVisibleColumns(screen, response.data.data)));
    return true;
  } catch (error) {
    dispatch(setPreference(screen, previous));
    dispatch(failure(error?.response?.data?.message || error?.message || MESSAGES.GENERAL_ERROR));
    return false;
  } finally {
    dispatch(setSaving(screen, false));
  }
};

/**
 * Load the saved visible columns of a screen.
 * GET /table-preferences/:screen
 */
const fetchTablePreference = (screen) => async (dispatch, getState) => {
  try {
    const response = await request.get(`table-preferences/${screen}`);

    // A save made while this request was in flight is newer than what the server just returned.
    if (getState().tablePreferencesReducers.screens[screen]?.loaded) return;

    dispatch(setPreference(screen, getVisibleColumns(screen, response.data.data)));
  } catch {
    // Not critical: the screen keeps showing the columns it has and asks again on the next visit.
  }
};

export { fetchTablePreference, saveTablePreference };
